import { Fragment } from 'react';
import type { ReactNode } from 'react';

type PolicyDocumentProps = {
  source: string;
};

function renderInline(value: string, keyPrefix: string): ReactNode[] {
  const pieces = value.split(/(\*\*[^*]+\*\*)/g).filter(Boolean);
  return pieces.map((piece, index) => {
    const key = `${keyPrefix}-${index}`;
    const bold = piece.startsWith('**') && piece.endsWith('**');
    const text = bold ? piece.slice(2, -2) : piece;
    const email = text === 'privacy@wellnessfrontier.ai';
    if (email) {
      const link = <a href="mailto:privacy@wellnessfrontier.ai">{text}</a>;
      return bold ? <strong key={key}>{link}</strong> : <Fragment key={key}>{link}</Fragment>;
    }
    return bold ? <strong key={key}>{text}</strong> : <Fragment key={key}>{text}</Fragment>;
  });
}

function isTableDivider(value: string) {
  return /^\|(?:\s*:?-+:?\s*\|)+$/.test(value);
}

function tableCells(value: string) {
  return value.slice(1, -1).split('|').map((cell) => cell.trim());
}

export function PolicyDocument({ source }: PolicyDocumentProps) {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: ReactNode[] = [];
  let index = lines[0]?.startsWith('# ') ? 1 : 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index]?.trim() ?? '';
    if (!line) { index += 1; continue; }

    if (line.startsWith('>')) {
      while (index < lines.length && (lines[index]?.trim().startsWith('>') ?? false)) index += 1;
      continue;
    }

    if (/^#{2,3}\s/.test(line)) {
      const level = line.match(/^#+/)?.[0].length ?? 2;
      const text = line.replace(/^#{2,3}\s+/, '');
      blocks.push(level === 2
        ? <h2 key={`block-${key++}`}>{text}</h2>
        : <h3 key={`block-${key++}`}>{text}</h3>);
      index += 1;
      continue;
    }

    if (line.startsWith('- ')) {
      const items: string[] = [];
      while (index < lines.length && (lines[index]?.trim().startsWith('- ') ?? false)) {
        let item = (lines[index]?.trim() ?? '').slice(2);
        index += 1;
        // A wrapped item continues on indented lines. They belong to the same
        // bullet, as in standard Markdown; ending the list at the first one
        // published the rest of the sentence as a separate paragraph.
        while (index < lines.length && /^\s+\S/.test(lines[index] ?? '') && !(lines[index]?.trim().startsWith('- ') ?? false)) {
          item += ` ${lines[index]!.trim()}`;
          index += 1;
        }
        items.push(item);
      }
      blocks.push(<ul key={`block-${key++}`}>{items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item, `list-${key}-${itemIndex}`)}</li>)}</ul>);
      continue;
    }

    if (line.startsWith('|') && (lines[index + 1] ? isTableDivider(lines[index + 1]!.trim()) : false)) {
      const headers = tableCells(line);
      index += 2;
      const rows: string[][] = [];
      while (index < lines.length && (lines[index]?.trim().startsWith('|') ?? false)) {
        rows.push(tableCells(lines[index]!.trim()));
        index += 1;
      }
      blocks.push(
        <div className="policy-document__table-wrap" key={`block-${key++}`} tabIndex={0}>
          <table>
            <thead><tr>{headers.map((header, cellIndex) => <th key={cellIndex} scope="col">{header}</th>)}</tr></thead>
            <tbody>{rows.map((row, rowIndex) => <tr key={rowIndex}>{row.map((cell, cellIndex) => <td key={cellIndex}>{renderInline(cell, `table-${rowIndex}-${cellIndex}`)}</td>)}</tr>)}</tbody>
          </table>
        </div>,
      );
      continue;
    }

    /*
     * Markdown's hard line break is two spaces at the end of a line, and every
     * one of these documents opens with one:
     *
     *     **Effective date:** September 1, 2026·· <- two spaces
     *     **Last updated:** September 7, 2026
     *
     * Joining wrapped lines with a space is right for prose and wrong here — it
     * published "Effective date: September 1, 2026 Last updated: September 7,
     * 2026" as a single run-on line at the top of all three policies, which
     * reads as a mistake in the one place a reader is checking a date.
     *
     * The trailing spaces have to be read off the RAW line; `line` above has
     * already been trimmed, which is why they were invisible here.
     */
    const hardBreak = (at) => /\s\s$/.test(lines[at] ?? '');
    const paragraph = [line];
    const breaks = [hardBreak(index)];
    index += 1;
    while (index < lines.length) {
      const next = lines[index]?.trim() ?? '';
      if (!next || next.startsWith('#') || next.startsWith('>') || next.startsWith('- ') || next.startsWith('|')) break;
      paragraph.push(next);
      breaks.push(hardBreak(index));
      index += 1;
    }

    // Only paragraphs that ASKED for a break are rendered line by line. Wrapped
    // prose keeps the single-string path, because rendering it per line would
    // split `**bold that wraps` across two renderInline calls and neither half
    // would match the `**…**` pair.
    const paragraphKey = key++;
    blocks.push(
      breaks.slice(0, -1).some(Boolean)
        ? (
          <p key={`block-${paragraphKey}`}>
            {paragraph.map((text, lineIndex) => (
              <Fragment key={lineIndex}>
                {renderInline(text, `paragraph-${paragraphKey}-${lineIndex}`)}
                {breaks[lineIndex] && lineIndex < paragraph.length - 1 ? <br /> : null}
              </Fragment>
            ))}
          </p>
        )
        : <p key={`block-${paragraphKey}`}>{renderInline(paragraph.join(' '), `paragraph-${paragraphKey}`)}</p>,
    );
  }

  return (
    <article className="policy-document">
      {blocks}
    </article>
  );
}
